import {Box, Flex, Text, Title} from 'exsportia-components'
import React, {FC, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import {StoreOrder, useStoreOrders} from '../../api/store'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {ORDER_STATUS_LOCALE, useCurrencyFormat} from './store-shared'

const isCurrent = (order: StoreOrder) =>
  order.orderStatus !== 'COMPLETED' && order.orderStatus !== 'CANCELLED'

const OrderRow: FC<{order: StoreOrder}> = ({order}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const format = useCurrencyFormat()
  const statusColor = order.orderStatus === 'CANCELLED'
    ? '#B3261E'
    : order.orderStatus === 'READY_FOR_PICKUP'
    ? '#1E7A4C'
    : order.orderStatus === 'COMPLETED'
    ? 'rgba(0, 16, 61, 0.48)'
    : '#8A6210'
  return (
    <Flex
      py='12px'
      gap='12px'
      cursor='pointer'
      alignItems='center'
      justifyContent='space-between'
      onClick={() => navigate(`../orders/${order.uid}`)}
    >
      <Flex
        minWidth='0'
        flexDirection='column'
      >
        <Text
          text={`${l('store-order', {caseAction: 'capitalize'})} ${order.reference || order.uid}`}
          settings={{fontWeight: 600}}
        />
        <Text
          text={`${order.lines.length} · ${format(order.totalPrice)}${order.isPaid ? '' : ` · ${l('store-unpaid', {caseAction: 'capitalize'})}`}`}
          settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
        />
      </Flex>
      <Text
        text={l(ORDER_STATUS_LOCALE[order.orderStatus], {caseAction: 'capitalize'})}
        settings={{fontSize: '13px', fontWeight: 600, color: statusColor}}
      />
    </Flex>
  )
}

export const StoreOrdersPage: FC = () => {
  const {l} = useLocale()
  const {data: orders, isLoading, error} = useStoreOrders(true)
  const [showPast, setShowPast] = useState(false)
  const filtered = useMemo(
    () => (orders || []).filter((order) => (showPast ? !isCurrent(order) : isCurrent(order))),
    [orders, showPast],
  )
  return (
    <GridLayout>
    <Box width='100%'>
      <Box py='8px'>
        <Title
          styleType='h1'
          text={l('store-orders', {caseAction: 'capitalize'})}
        />
      </Box>
      <Flex
        gap='8px'
        py='8px'
      >
        <Button
          type={showPast ? 'secondary' : 'primary'}
          onClick={() => setShowPast(false)}
        >
          {l('store-current', {caseAction: 'capitalize'})}
        </Button>
        <Button
          type={showPast ? 'primary' : 'secondary'}
          onClick={() => setShowPast(true)}
        >
          {l('store-past', {caseAction: 'capitalize'})}
        </Button>
      </Flex>
      {error && (
        <Box py='24px'>
          <Text
            text={l('store-signin-orders', {caseAction: 'capitalize'})}
            settings={{color: 'rgba(0, 16, 61, 0.48)'}}
          />
        </Box>
      )}
      {filtered.map((order) => (
        <OrderRow
          key={order.uid}
          order={order}
        />
      ))}
      {!isLoading && !error && filtered.length === 0 && (
        <Box py='24px'>
          <Text
            text={l('store-no-orders', {caseAction: 'capitalize'})}
            settings={{color: 'rgba(0, 16, 61, 0.48)'}}
          />
        </Box>
      )}
    </Box>
    </GridLayout>
  )
}
